import { Box, Flex, Text } from '@chakra-ui/react'
import { Select, SelectOption } from 'components/Select'
import { FiltersT } from '.'
import { useCallback, useMemo, useTransition } from 'react'
import { DateRangePicker } from 'components/DateRangePicker'
import { formatDate } from './formatDate'
import { getCountryName } from './getCountryName'
import { MultiSelectDropdown } from 'components/MultiSelect'
import useHospitalisationsData from './apis/useHospitalisationsData'
import useIliAriSariData from './apis/useIliAriSariData'
import useTestPositivityData from './apis/useTestPositivityData'
import { parseDate } from './helpers'
import { CustomOption } from 'components/MultiSelect/CustomOption'
import useTracking from 'tracking/useTracking'

function generateOptions(
  options: any[],
  selected: {
    label: string
    value: string | number
  }[]
) {
  const _options = options
    .filter((item) => !item.disabled)
    .map((x) => ({
      value: x,
      label: x,
      isSelected: selected.some((item) => item.value === x),
    }))
    .sort((a, b) => {
      if (a.isSelected && !b.isSelected) return -1
      if (!a.isSelected && b.isSelected) return 1
      return a.label.localeCompare(b.label)
    })

  return _options
}

const MainFilterSection = ({
  countries,
  diseaseOptions,
  filters,
  handleFilters,
  handleCountry,
  isSingleType,
}: {
  countries: string[]
  diseaseOptions: string[]
  handleFilters: (filters: FiltersT) => void
  handleCountry: (country: string) => void
  filters: FiltersT
  isSingleType?: string
}) => {
  const [_, startTransition] = useTransition()
  const [tracking] = useTracking()

  const { data: data1 } = useHospitalisationsData()
  const { data: data2 } = useIliAriSariData()
  const { data: data3 } = useTestPositivityData()

  const allData = useMemo(
    () => [data1, data2, data3].filter((x) => !!x).flat(),
    [data1, data2, data3]
  )
  const startRange = useMemo(
    () =>
      Array.from(new Set(allData.map((x) => parseDate(x.date_start)))).sort(
        (a, b) => a.getTime() - b.getTime()
      )[0],
    [allData]
  )
  const endRange = useMemo(
    () =>
      Array.from(new Set(allData.map((x) => parseDate(x.date_end)))).sort(
        (a, b) => b.getTime() - a.getTime()
      )[0],
    [allData]
  )

  const onDataTypeChange = useCallback(
    (e: SelectOption<any>[]) => {
      tracking.diseaseFilterChanged({
        diseases: e.map((x) => x.label).join(', '),
      })
      startTransition(() => {
        handleFilters({ ...filters, dataType: e })
      })
    },
    [filters, handleFilters, tracking]
  )

  const onCountryChange = useCallback(
    (e: any) => {
      startTransition(() => {
        if (e.value === 'global') {
          handleCountry(filters.country)
        }
        handleCountry(e.value)
      })
    },
    [filters.country, handleCountry]
  )
  const onDateChange = useCallback(
    (start: string, end: string) => {
      startTransition(() => {
        handleFilters({ ...filters, dateRange: { start, end } })
      })
    },
    [filters, handleFilters]
  )

  const countryOptions = useMemo(
    () => [
      // Always show selected option on the top
      { value: filters.country, label: getCountryName(filters.country) },
      ...countries
        .filter((x) => x !== filters.country)
        .sort((a, b) => {
          const countryA = getCountryName(a)
          const countryB = getCountryName(b)
          return countryA.localeCompare(countryB)
        })
        .map((item) => ({
          value: item ?? '',
          label: getCountryName(item ?? ''),
        })),
    ],
    [countries, filters.country]
  )

  return (
    <Flex
      w='full'
      border={'1px'}
      borderColor={'gray.200'}
      bg='gray.100'
      px={3}
      py={1}
      borderRadius={'md'}
      justifyContent={'space-between'}
      fontSize={'sm'}
    >
      <Flex alignItems={'center'}>
        <Box>Diseases</Box>
        {!isSingleType ? (
          <Box textTransform='capitalize' mx='8px' bg='white'>
            <MultiSelectDropdown
              value={filters.dataType}
              updateFilter={onDataTypeChange}
              options={generateOptions(diseaseOptions, filters.dataType)}
              showAllOption
            />
          </Box>
        ) : (
          <Text mx={2}>{isSingleType}</Text>
        )}
        <Text>over</Text>
        <DateRangePicker
          startRange={startRange}
          endRange={endRange}
          dates={filters.dateRange}
          onDateChange={onDateChange}
          formatDate={(x) => (x ? formatDate(x) : '')}
        />
        <Text>in</Text>
        <Box textTransform='capitalize' mx='8px' bg='white'>
          <Select
            value={{
              value: filters?.country,
              label: getCountryName(filters?.country) || filters?.country,
            }}
            options={countryOptions}
            onChange={onCountryChange}
            isMulti={false}
            isSearchable={true}
            height='26px'
            CustomOption={CustomOption}
          />
        </Box>
      </Flex>
    </Flex>
  )
}

export default MainFilterSection
